/**
 * Shift Conversation API client
 * Hospital-initiated messaging for doctor applications
 */

import axios from "../utils/index";
import {
  ContextType,
  CreateConversationRequest,
  CreateConversationResponse,
  CreateDocRequestPayload,
  CreateDocRequestResponse,
  SendMessageRequest,
  SendMessageResponse,
  ListMessagesResponse,
  ListConversationsResponse,
  ListDocRequestsResponse,
  MarkConversationReadResponse,
  UnreadCountResponse,
  CloseConversationResponse,
  GetConversationDetailResponse,
  ShiftApplication,
} from "../interface/shiftConversation";

const BASE_PATH = "/v1/hospital/shift-conversations";

type ListHospitalConversationsParams = {
  status?: string;
  contextType?: ContextType;
  doctorId?: string;
};

type ConversationUnreadCountParams = {
  contextType?: ContextType;
};

/**
 * List conversations for current hospital (rich response with doctor + lastMessage).
 */
export const listHospitalConversations =
  async (
    params?: ListHospitalConversationsParams,
  ): Promise<ListConversationsResponse> => {
    const response = await axios.get<ListConversationsResponse>(BASE_PATH, {
      params: { ...(params || {}), _t: Date.now() },
    });
    return response.data;
  };

/**
 * Get total unread count across all conversations.
 */
export const getConversationUnreadCount =
  async (
    params?: ConversationUnreadCountParams,
  ): Promise<UnreadCountResponse> => {
    const response = await axios.get<UnreadCountResponse>(
      `${BASE_PATH}/unread-count`,
      {
        params: { ...(params || {}), _t: Date.now() },
      },
    );
    return response.data;
  };

/**
 * Create or get an existing conversation
 * Returns existing conversation if one already exists for this doctor + group
 */
export const createOrGetConversation = async (
  doctorId: string,
  shiftGroupId: string,
  shiftApplications: ShiftApplication[],
  contextType?: ContextType,
): Promise<CreateConversationResponse> => {
  const payload: CreateConversationRequest = {
    doctorId,
    shiftGroupId,
    shiftApplications,
    contextType,
  };

  const response = await axios.post<CreateConversationResponse>(
    `${BASE_PATH}/create-or-get`,
    payload,
  );
  return response.data;
};

export const createOrGetStaffConversation = async (
  doctorId: string,
): Promise<CreateConversationResponse> => {
  const response = await axios.post<CreateConversationResponse>(
    `${BASE_PATH}/create-or-get-staff`,
    { doctorId },
  );
  return response.data;
};

/**
 * Add more linked applications to an existing conversation
 */
export const addShiftsToConversation = async (
  conversationId: string,
  shiftApplications: ShiftApplication[],
): Promise<CreateConversationResponse> => {
  const payload = { shiftApplications };
  const response = await axios.post<CreateConversationResponse>(
    `${BASE_PATH}/${conversationId}/shifts`,
    payload,
  );
  return response.data;
};

/**
 * Send a message from hospital to doctor
 */
export const sendConversationMessage = async (
  conversationId: string,
  messageText: string,
): Promise<SendMessageResponse> => {
  const payload: SendMessageRequest = { messageText };
  const response = await axios.post<SendMessageResponse>(
    `${BASE_PATH}/${conversationId}/messages`,
    payload,
  );
  return response.data;
};

/**
 * List all messages in a conversation
 */
export const listConversationMessages = async (
  conversationId: string,
  pageNo: number = 1,
  pageSize: number = 50,
  markAsRead: boolean = false,
): Promise<ListMessagesResponse> => {
  const response = await axios.get<ListMessagesResponse>(
    `${BASE_PATH}/${conversationId}/messages`,
    {
      params: { pageNo, pageSize, markAsRead },
    },
  );
  return response.data;
};

/**
 * Explicitly mark all messages in a conversation as read for hospital-side counters.
 */
export const markConversationAsRead = async (
  conversationId: string,
): Promise<MarkConversationReadResponse> => {
  const response = await axios.post<MarkConversationReadResponse>(
    `${BASE_PATH}/${conversationId}/read`,
  );
  return response.data;
};

/**
 * Close a conversation manually (hospital only)
 */
export const closeConversation = async (
  conversationId: string,
): Promise<CloseConversationResponse> => {
  const response = await axios.post<CloseConversationResponse>(
    `${BASE_PATH}/${conversationId}/close`,
  );
  return response.data;
};

/**
 * Reopen a closed conversation (hospital only)
 */
export const reopenConversation = async (
  conversationId: string,
): Promise<CloseConversationResponse> => {
  const response = await axios.post<CloseConversationResponse>(
    `${BASE_PATH}/${conversationId}/reopen`,
  );
  return response.data;
};

/**
 * Get full conversation detail including linked shift applications.
 * Used to resolve shiftId for opening the applicant details modal.
 */
export const getConversationDetail = async (
  conversationId: string,
): Promise<GetConversationDetailResponse> => {
  const response = await axios.get<GetConversationDetailResponse>(
    `${BASE_PATH}/${conversationId}`,
  );
  return response.data;
};

/**
 * Create a document request in a conversation (hospital only).
 */
export const createConversationDocRequest = async (
  conversationId: string,
  payload: CreateDocRequestPayload,
): Promise<CreateDocRequestResponse> => {
  const response = await axios.post<CreateDocRequestResponse>(
    `${BASE_PATH}/${conversationId}/doc-requests`,
    payload,
  );
  return response.data;
};

/**
 * List document requests in a conversation.
 */
export const listConversationDocRequests = async (
  conversationId: string,
): Promise<ListDocRequestsResponse> => {
  const response = await axios.get<ListDocRequestsResponse>(
    `${BASE_PATH}/${conversationId}/doc-requests`,
    {
      params: { _t: Date.now() },
    },
  );
  return response.data;
};

/**
 * Remove a pending document request item (hospital only).
 */
export const deleteConversationDocRequestItem = async (
  conversationId: string,
  requestId: string,
  itemId: string,
): Promise<{ code: number; data?: boolean; message?: string }> => {
  const response = await axios.delete<{ code: number; data?: boolean; message?: string }>(
    `${BASE_PATH}/${conversationId}/doc-requests/${requestId}/items/${itemId}`,
  );
  return response.data;
};
